import * as React from 'react';
// @ts-expect-error
import type {ActionMeta} from 'react-select';

import type {SelectComponentValue} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {SelectInline} from 'shells/select-inline/select-inline';

import type {EmailAddress as EmailAddressFragment} from 'nutshell-graphql-types';

import {createSelectOptionsFromEmailAddresses} from './create-select-options-from-email-addresses';
import {styleOptionForEntityType} from './style-option-for-entity-type';

import styles from './email-selection.module.css';

type Props = {
    selectedAddresses: EmailAddressFragment[];
    onChange: (newOptions: EmailAddressFragment[]) => any;
    onFocus?: () => void;
    onBlur?: () => void;
    onShowCC?: () => void;
    availableAddresses: EmailAddressFragment[];
    errorMessage?: string;
};

export class EmailToSelection extends React.PureComponent<Props> {
    scrollNode: HTMLElement | null | undefined;

    render() {
        const {onShowCC} = this.props;
        const optionsForSelect = createSelectOptionsFromEmailAddresses(
            this.props.availableAddresses
        );
        const selectedAddresses = createSelectOptionsFromEmailAddresses(
            this.props.selectedAddresses
        );
        // TODO: move this to something that finds it once, not each render
        const portalTarget =
            document.querySelector('#email-cc-field') ||
            document.querySelector('#email-subject-and-body-fields');

        return (
            <div
                ref={(node) => {
                    this.scrollNode = node;
                }}
                className={
                    portalTarget ? styles['email-selection--scrollable'] : styles['email-selection']
                }
            >
                <SelectInline
                    errorMessage={this.props.errorMessage}
                    placeholder='Choose a recipient…'
                    noOptionsMessage={() => 'Only existing addresses are supported in this field.'}
                    tokenizedStyle='light'
                    options={optionsForSelect}
                    value={selectedAddresses}
                    onChange={this.handleChange}
                    inlineLabel='To'
                    onFocus={this.props.onFocus}
                    onBlur={this.props.onBlur}
                    multi={true}
                    // @ts-expect-error
                    menuPortalTarget={portalTarget || undefined}
                    menuPortalStyles={
                        portalTarget
                            ? // @ts-expect-error
                              (base) => ({
                                  ...base,
                                  left: 64,
                                  top: 0,
                                  zIndex: 10,
                              })
                            : undefined
                    }
                    rightContents={
                        onShowCC ? (
                            <Button
                                variant='text-primary'
                                onClick={onShowCC}
                                style={{position: 'absolute', right: 8}}
                            >
                                Cc
                            </Button>
                        ) : null
                    }
                    styles={{option: styleOptionForEntityType}}
                />
            </div>
        );
    }

    /*
     * Handler here to return the actual email address objects instead
     * of the select options. We need to pass that information
     * around so our parent component knows which email addresses (and
     * their associated entities) are selected, so we can apply the right
     * information to the potentially selected email template
     */
    handleChange = (selectedOptions: SelectComponentValue[], actionMeta: ActionMeta) => {
        // Quickly run through these and key them by their email address,
        // so we're not doing multiple searches
        const optionsKeyedByAddress: {
            [key: string]: EmailAddressFragment;
        } = this.props.availableAddresses.reduce<Record<string, any>>((acc, opt) => {
            return {...acc, [opt.value.address]: opt};
        }, {});

        // Gotta preserve the order of selected options. This might
        // be sub-optimal, but we're talking only a few email addresses
        const newSelectedAddresses = selectedOptions.map<EmailAddressFragment | null | undefined>(
            (opt) => {
                return optionsKeyedByAddress[opt.id];
            }
        );

        // Not sure why flow isn't complaining, but we need to filter
        // out undefined values if the above key lookup failed
        const nonNullNewAddresses = newSelectedAddresses.filter(Boolean);

        // If we've added a new option, make sure we scroll to the bottom and show it
        if (actionMeta.action === 'select-option') {
            setTimeout(() => {
                if (this.scrollNode) {
                    this.scrollNode.scrollTop = this.scrollNode.scrollHeight;
                }
            }, 0);
        }

        // @ts-expect-error
        this.props.onChange(nonNullNewAddresses);
    };
}
